import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import {
  Badge,
  Button,
  Col,
  Row,
  UncontrolledTooltip,
} from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"

import { useListParams } from "hooks/useListParams"
import {
  useInventoryAccessories,
  useDeleteInventoryAccessory,
} from "hooks/useInventoryAccessory"

import { useAuth } from "hooks/authHook"
import { ACCESS } from "constants/accessPermissions"

import InventoryAccessoryAddEdit from "./InventoryAccessoryAddEdit"

const InventoryAccessoryList = () => {
  document.title = "Inventory Accessories"

  const { hasAnyPermission } = useAuth()
  const canAdd = hasAnyPermission([ACCESS.INVENTORY_ACCESSORY.ADD])
  const canEdit = hasAnyPermission([ACCESS.INVENTORY_ACCESSORY.EDIT])
  const canDelete = hasAnyPermission([ACCESS.INVENTORY_ACCESSORY.DELETE])

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 25, search } = params
    return { page, per_page, search }
  }, [params])

  const {
    data: responseData,
    isLoading,
    refetch,
    isRefetching,
  } = useInventoryAccessories(queryParams)

  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteInventoryAccessory()

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Accessory deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (err) {
      console.error(err)
      toast.error("Failed to delete accessory.")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        size: 60,
        cell: ({ row }) => (
          <div className="text-muted fw-semibold">#{row.original.id}</div>
        ),
      },
      {
        header: "Name",
        accessorKey: "name",
        cell: ({ row }) => (
          <div className="fw-semibold">{row.original.name}</div>
        ),
      },
      {
        header: "Sort",
        accessorKey: "sort_order",
        size: 80,
        cell: ({ row }) => (
          <span className="text-muted">{row.original.sort_order ?? "-"}</span>
        ),
      },
      {
        header: "Status",
        accessorKey: "is_active",
        size: 100,
        cell: ({ row }) =>
          row.original.is_active ? (
            <Badge color="success">Active</Badge>
          ) : (
            <Badge color="secondary">Inactive</Badge>
          ),
      },
      {
        header: "Description",
        accessorKey: "description",
        cell: ({ row }) => (
          <div className="text-muted" style={{ maxWidth: 260 }}>
            {row.original.description || "-"}
          </div>
        ),
      },
      {
        header: "Action",
        size: 120,
        cell: ({ row }) => {
          const data = row.original
          return (
            <div className="d-flex gap-1">
              {canEdit && (
                <>
                  <Button
                    color="info"
                    size="sm"
                    onClick={() => handleEditClick(data)}
                    id={`edit-${data.id}`}
                  >
                    <i className="mdi mdi-pencil-outline" />
                  </Button>
                  <UncontrolledTooltip target={`edit-${data.id}`}>
                    Edit
                  </UncontrolledTooltip>
                </>
              )}
              {canDelete && (
                <>
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => handleDeleteClick(data)}
                    id={`delete-${data.id}`}
                  >
                    <i className="mdi mdi-delete-outline" />
                  </Button>
                  <UncontrolledTooltip target={`delete-${data.id}`}>
                    Delete
                  </UncontrolledTooltip>
                </>
              )}
            </div>
          )
        },
      },
    ],
    [canEdit, canDelete]
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper
      showBreadcrumb={true}
      title="Inventory Accessories"
      breadcrumbItem="Inventory Accessories"
    >
      <div className="d-flex align-items-center">
        <h5 className="mb-0 mt-3 card-title flex-grow-1">
          Total{" "}
          <Badge className="badge-soft-info">
            {paginationInfo?.total ?? 0}
          </Badge>
        </h5>
        <div className="flex-shrink-0">
          {canAdd && (
            <Button color="primary" onClick={handleAddNewClick}>
              Add New
            </Button>
          )}
          <Button color="light" onClick={refetch} className="ms-1">
            <i
              className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`}
            />
          </Button>
          {hasActiveFilters && (
            <Button color="danger" onClick={resetParams} className="ms-1">
              Reset Filters
            </Button>
          )}
        </div>
      </div>

      <DeleteModal
        show={deleteModal}
        loading={deleteLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        className="table-responsive"
      />

      <InventoryAccessoryAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
      />
    </PageWrapper>
  )
}

export default InventoryAccessoryList
